<div class="role7-130000-form-container">
  <p-toast></p-toast>

  <app-sheet-loader [visible]="isLoading" />

  <!-- Form Content -->
  <p-card [header]="formTitle" [subheader]="isDev ? '[130000] Disclosure - Auditors report to members' : ''" *ngIf="!isLoading && schema && auditorsReportForm" class="form-card">
    
    <div class="bg-gray-200 text-black-700 text-sm font-medium px-2 py-1">Disclosure on auditors report to members</div>
    <div class="bg-gray-200 text-black-700 text-sm font-medium px-3 py-1">Auditor's Report</div>
    <!-- Financial-period date range header (current FY) — sourced from Filing Info DEI via CrossReferenceService -->
    <app-financial-period-band [periods]="['current']"></app-financial-period-band>

    <form
      [formGroup]="auditorsReportForm"
      appFieldLinkTarget
      fieldLinkSheet="auditors-report"
      [fieldLinkControls]="schema.controls"
    >
      <!-- Dynamic Fields (excluding auditor table fields) -->
      <ng-container *ngFor="let control of schema.controls">
        <div class="form-field" [attr.data-qname]="control.qname" *ngIf="shouldShowField(control) && !control.ui.auditorTable">
          <label class="field-label" [class.required]="isFieldRequired(control)">
            {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
            <!-- <span class="warning-mark" *ngIf="isEditedMode() && hasWarnings(control.qname)">*</span> -->
          </label>

          <!-- Text Input -->
          <div class="field-input-wrapper" *ngIf="control.ui.control === 'text'">
            <input
              type="text"
              pInputText
              [formControlName]="control.qname"
              [placeholder]="control | placeholder"
              [pKeyFilter]="getKeyFilter(control)"
              [maxlength]="getMaxLength(control.qname)"
              class="w-full"
            />

            <small class="error-message"
                   *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
              <span *ngIf="getControl(control.qname)?.errors?.['required']">
                {{ getCustomErrorMessage(control.qname, 'required') || (control.label + ' is required') }}
              </span>
              <span *ngIf="getControl(control.qname)?.errors?.['pattern']">
                {{ getCustomErrorMessage(control.qname, 'pattern') || 'Invalid format. ' + control.ui.placeholder }}
              </span>
            </small>

            <!-- Warning/Error messages - Non-blocking suggestions -->
            <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
          </div>

          <!-- Textarea -->
          <div class="field-input-wrapper" *ngIf="control.ui.control === 'textarea'">
            <textarea
              pTextarea
              [formControlName]="control.qname"
              [placeholder]="control | placeholder"
              [rows]="control.ui.rows || 5"
              class="w-full"
              [class.p-invalid]="getControl(control.qname)?.invalid && getControl(control.qname)?.touched"
            ></textarea>

            <small class="error-message"
                   *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
              <span *ngIf="getControl(control.qname)?.errors?.['required']">
                {{ getCustomErrorMessage(control.qname, 'required') || (control.label + ' is required') }}
              </span>
            </small>

            <!-- Warning/Error messages - Non-blocking suggestions -->
            <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
          </div>

          <!-- Rich-text (WYSIWYG) field — same preview + pop-out editor as role
               750000 (AS-897). NOTE: this field's schema type is
               `xbrli:stringItemType`, not `TextBlock`; converted control-only on
               request — see the role 7 spec's [Discrepancy Detected] note re: the
               XBRL contract. The sheet's other textarea fields stay plain. -->
          <div class="field-input-wrapper" *ngIf="control.ui.control === 'richtext'">
            <app-rich-text-field
              [formControlName]="control.qname"
              [title]="control.label"
              [placeholder]="control | placeholder: 'Click to add content…'"
              [invalid]="!!(getControl(control.qname)?.invalid && getControl(control.qname)?.touched)"
            ></app-rich-text-field>

            <small class="error-message"
                   *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
              <span *ngIf="getControl(control.qname)?.errors?.['required']">
                {{ getCustomErrorMessage(control.qname, 'required') || (control.label + ' is required') }}
              </span>
            </small>

            <!-- Warning/Error messages - Non-blocking suggestions -->
            <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
          </div>

          <!-- Select/Dropdown -->
          <div class="field-input-wrapper" *ngIf="control.ui.control === 'select'">
            <p-select
              [inputId]="control.qname"
              [formControlName]="control.qname"
              [options]="getDropdownOptions(control.ui.enumSource)"
              optionLabel="label"
              optionValue="value"
              [placeholder]="control | fixedPlaceholder: 'Please select'"
              class="w-full"
              [filter]="true"
              filterPlaceholder="Search..."
              [showClear]="true"
            ></p-select>

            <small class="error-message"
                   *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
              <span *ngIf="getControl(control.qname)?.errors?.['required']">
                {{ getCustomErrorMessage(control.qname, 'required') || (control.label + ' is required') }}
              </span>
            </small>

            <!-- Warning/Error messages - Non-blocking suggestions -->
            <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
          </div>

          <!-- Date Picker -->
          <div class="field-input-wrapper" *ngIf="control.ui.control === 'date'">
            <p-datepicker
              [inputId]="control.qname"
              [formControlName]="control.qname"
              dateFormat="yy-mm-dd"
              [showIcon]="true"
              iconDisplay="input"
              [placeholder]="control | fixedPlaceholder: 'Select a date'"
              class="w-full"
              [class.p-invalid]="getControl(control.qname)?.invalid && getControl(control.qname)?.touched"
              (onInput)="onInputChange($event)"
            ></p-datepicker>

            <small class="error-message"
                   *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
              <span *ngIf="getControl(control.qname)?.errors?.['required']">
                {{ getCustomErrorMessage(control.qname, 'required') || (control.label + ' is required') }}
              </span>
            </small>

            <!-- Warning/Error messages - Non-blocking suggestions -->
            <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
          </div>
        </div>
      </ng-container>

      <!-- Auditor Information Table -->
      <div class="form-section mt-6" *ngIf="getAuditorTableFields().length > 0">
        <h3 class="section-header">Auditor Information</h3>

        <table class="auditor-table">
          <thead>
            <tr>
              <th style="width: 40%">Field</th>
              <th style="width: 60%">0001<app-required-marker [when]="true" /></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let rowKey of getAuditorTableRows()" [attr.data-qname]="getAuditorRowQname(rowKey)">
              <td class="auditor-row-label">{{ getAuditorRowLabel(rowKey) }}<app-required-marker [when]="isAuditorRowRequired(rowKey)" /></td>
              <td>
                <ng-container *ngFor="let control of getAuditorTableFieldsByRow(rowKey)">
                  <!-- Text Input for Auditor Table -->
                  <input
                    *ngIf="control.ui.control === 'text'"
                    type="text"
                    pInputText
                    [formControlName]="control.qname"
                    [placeholder]="control | placeholder"
                    class="w-full"
                    [class.p-invalid]="getControl(control.qname)?.invalid && getControl(control.qname)?.touched"
                    [pKeyFilter]="getKeyFilter(control)"
                    [maxlength]="getMaxLength(control.qname)"
                    (input)="control.ui.inputTransform ? applyInputTransform($event, control) : null"
                    (paste)="control.ui.inputTransform ? onPasteInputTransform($event, control) : null"
                  />

                  <!-- Select/Dropdown for Auditor Table -->
                  <p-select
                    *ngIf="control.ui.control === 'select'"
                    [inputId]="control.qname"
                    [formControlName]="control.qname"
                    [options]="getDropdownOptions(control.ui.enumSource)"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="control | fixedPlaceholder: 'Please select'"
                    class="w-full"
                    [filter]="true"
                    filterPlaceholder="Search..."
                    [class.p-invalid]="getControl(control.qname)?.invalid && getControl(control.qname)?.touched"
                    [showClear]="true"
                  ></p-select>

                  <!-- Error Messages -->
                  <small class="error-message"
                         *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                    <span *ngIf="getControl(control.qname)?.errors?.['required']">
                      {{ getCustomErrorMessage(control.qname, 'required') || (control.label + ' is required') }}
                    </span>
                    <span *ngIf="getControl(control.qname)?.errors?.['pattern']" [innerHTML]="getCustomErrorMessage(control.qname, 'pattern') || 'Invalid format'">
                    </span>
                  </small>
                </ng-container>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </form>

    <!-- Form Status -->
    <!-- <div *ngIf="!isLoading && schema && auditorsReportForm" class="form-status mt-4">
      <p>
        <strong>Form Status:</strong>
        <span [class]="auditorsReportForm.valid ? 'text-green-600' : 'text-red-600'">
          {{ auditorsReportForm.valid ? 'Valid ✓' : 'Invalid - Please fill required fields' }}
        </span>
      </p>
      <p>
        <strong>Total Fields:</strong> {{ schema.controls.length || 0 }}
      </p>
      <p>
        <strong>Auto-save:</strong>
        <span class="text-green-600">Enabled (500ms debounce)</span>
      </p>
    </div> -->
     <submit-for-validation
      [form]="auditorsReportForm"
      [sheetKey]="'auditorsReport'"
      [formTitle]="formTitle"
      (cancelConfirmed)="onCancelConfirmed()"
    ></submit-for-validation>
  </p-card>
</div>
